import React, { createContext, useContext, ReactNode, useEffect, useState } from 'react';
import {
  buildPayloadFromConditionForm,
  type ConditionFormValues,
} from 'utils/professionalQualifications';
import useProfessionalQualifications from 'hooks/useProfessionalQualifications';
import useMissingConditionFormValues from 'components/credentials/conditionDialogs/useMissingConditionFormValues';
import type { ICondition, IQualification } from '@medely/web-components/types';
import { useTrackQualification } from 'hooks/useTrackQualification';
import { gateNames, useGate } from '@mp';
import { MissingConditionFormOption } from './MissingConditionFormDialog';

export type MissingConditionFormQualification =
  MissingConditionFormOption['qualifications'][number];

interface IMissingConditionFormProps {
  children: (props: {
    condition: MissingConditionFormOption | undefined;
    isActionable: boolean;
    qualificationAutomationId?: number;
    submit: (values: {
      condition: ConditionFormValues;
      qualification_automation_id?: number;
    }) => Promise<void>;
  }) => ReactNode;
  condition: ICondition;
  isSimpleCondition: boolean;
  missingOptions: MissingConditionFormOption[];
  onQualificationsResolve?: () => void;
  selectedOption?: MissingConditionFormOption;
  setSelectedProfessionalQualificationId: (_: number) => void;
  setSelectedOption: (_: MissingConditionFormOption | undefined) => void;
}

type MissingConditionFormContext = {
  condition?: ICondition;
  options: MissingConditionFormOption[];
  selectedOption?: MissingConditionFormOption;
  selectedQualificationIndex: number;
  selectOption: (
    option: MissingConditionFormOption,
    qualification: MissingConditionFormQualification,
  ) => void;
};

const MissingConditionFormContext = createContext<MissingConditionFormContext>({
  options: [],
  selectedQualificationIndex: 0,
  selectOption: () => null,
});

export const useMissingConditionForm = () => useContext(MissingConditionFormContext);

const MissingConditionForm = ({
  children,
  condition,
  isSimpleCondition,
  missingOptions,
  onQualificationsResolve,
  selectedOption,
  setSelectedProfessionalQualificationId,
  setSelectedOption,
}: IMissingConditionFormProps) => {
  const { submitProfessionalQualification } = useProfessionalQualifications();
  const [selectedQualificationIndex, setSelectedQualificationIndex] = useState<number>(0);
  const { values: conditionFormValues } = useMissingConditionFormValues(selectedOption);
  const { trackQualificationFinishEditing } = useTrackQualification();
  const selectedQualification = selectedOption?.qualifications?.[selectedQualificationIndex];
  const isActionable = isSimpleCondition || Boolean(selectedQualification);
  const automationGateOpen = useGate(gateNames.qualificationAutomationEnabled);
  const qualificationAutomationId = automationGateOpen
    ? selectedQualification?.qualification_automations?.[0]?.id
    : undefined;

  const submit = async (values: {
    condition: ConditionFormValues;
    qualification_automation_id?: number;
  }) => {
    const payload = buildPayloadFromConditionForm(
      values.condition,
      selectedQualificationIndex,
      true,
      values.qualification_automation_id,
    );

    await submitProfessionalQualification(payload);

    trackQualificationFinishEditing({
      qualificationId: selectedQualification?.id,
      saved: true,
    });

    onQualificationsResolve?.();
  };

  const selectOption = (option: MissingConditionFormOption, qualification: IQualification) => {
    if (option) {
      const selectedProfessionalQualificationId = qualification
        ? option.qualificationToProfessionalQualification[qualification.id]?.id
        : option.qualificationToProfessionalQualification[option.qualifications[0].id]?.id;
      const currentIndex = Math.max(0, option.qualifications.indexOf(qualification));
      setSelectedQualificationIndex(currentIndex);
      setSelectedProfessionalQualificationId(selectedProfessionalQualificationId);
    }

    setSelectedOption(option);
  };

  useEffect(() => {
    if (!missingOptions.length) {
      onQualificationsResolve?.();
    } else if (isSimpleCondition) {
      selectOption(missingOptions[0], missingOptions[0].qualifications[0]);
    }
  }, [missingOptions.length]);

  return (
    <MissingConditionFormContext.Provider
      value={{
        condition,
        options: missingOptions,
        selectedOption,
        selectOption,
        selectedQualificationIndex,
      }}
    >
      {children({
        condition: conditionFormValues,
        qualificationAutomationId,
        submit,
        isActionable,
      })}
    </MissingConditionFormContext.Provider>
  );
};

export default MissingConditionForm;
